/* eslint-disable complexity */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import RadioButton, { RadioButtonGroup } from 'carbon-react/lib/components/radio-button';
import Switch from 'carbon-react/lib/components/switch';
import Box from 'carbon-react/lib/components/box';
import Typography from 'carbon-react/lib/components/typography';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { Logger } from '@futrli/shared-services';

import * as styles from './bulk-update.scss';
import { bulkUpdateForecast } from '../../../core/api/forecast';
import { BulkUpdatedPredictionsPayload } from './types';
import { trackEvent } from '../../../core/tracker';
import { TRACKING_EVENTS } from '../../../core/constants';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { AssistantSidePanelFormContainer } from '../assistant-side-panle-form-container';

interface Props {
  onClose: () => void;
}

export const BulkUpdatePredictions = ({ onClose }: Props) => {
  const intl = useIntl();

  const [bulkType, setBulkType] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [incomeIncluded, setIncomeIncluded] = useState(false);
  const [costOfSalesIncluded, setCostOfSalesIncluded] = useState(false);
  const [expensesIncluded, setExpensesIncluded] = useState(false);
  const [otherIncomeIncluded, setOtherIncomeIncluded] = useState(false);
  const [otherExpensesIncluded, setOtherExpensesIncluded] = useState(false);

  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';

  const {
    forecast,
    fetchForecastReport,
    fetchForecast,
    isConsolidation,
    bulkUpdateAssumption
  } = usePredictionsInfoContext();

  const showAutoPredictionsOnOff = !!forecast?.include_ai_assumptions;

  useEffect(() => {
    if (showAutoPredictionsOnOff) {
      setBulkType('auto-predictions');
    } else {
      setBulkType('manual-predictions');
    }
  }, [showAutoPredictionsOnOff]);

  const onRadioClick = (type: string) => {
    setBulkType(type);
  };

  const handleClose = () => {
    onClose();
  };

  const onRecalculateClick = async () => {
    if (!forecast) {
      return;
    }

    setIsLoading(true);
    const data: BulkUpdatedPredictionsPayload = {
      ai_assumptions_by_category: {},
      manual_assumptions_by_category: {}
    };
    if (bulkType === 'auto-predictions') {
      data.ai_assumptions_by_category = {
        income: { excluded_from_report: !incomeIncluded },
        cost_of_sales: { excluded_from_report: !costOfSalesIncluded },
        expenses: { excluded_from_report: !expensesIncluded },
        other_income: { excluded_from_report: !otherIncomeIncluded },
        other_expenses: { excluded_from_report: !otherExpensesIncluded }
      };
      delete data.manual_assumptions_by_category;
    } else {
      data.manual_assumptions_by_category = {
        income: { excluded_from_report: !incomeIncluded },
        cost_of_sales: { excluded_from_report: !costOfSalesIncluded },
        expenses: { excluded_from_report: !expensesIncluded },
        other_income: { excluded_from_report: !otherIncomeIncluded },
        other_expenses: { excluded_from_report: !otherExpensesIncluded }
      };
      delete data.ai_assumptions_by_category;
    }

    try {
      if (isConsolidation) {
        await bulkUpdateAssumption?.(data, workspaceId, organisationId, forecast?.id);
      } else {
        await bulkUpdateForecast(data, workspaceId, organisationId, forecast?.id);
        fetchForecastReport?.('all');
        fetchForecast?.();
      }
      handleClose();
      trackEvent(TRACKING_EVENTS.BULK_ACTIONED_AUTO_PREDICTIONS);
    } catch (e) {
      Logger.error(e as string);
    } finally {
      setIsLoading(false);
    }
  };

  const handleIncomeIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncomeIncluded(event.target.checked);
  };

  const handleCostOfSalesIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCostOfSalesIncluded(event.target.checked);
  };

  const handleExpensesIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setExpensesIncluded(event.target.checked);
  };

  const handleOtherExpensesIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherExpensesIncluded(event.target.checked);
  };

  const handleOtherIncomeIncludedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtherIncomeIncluded(event.target.checked);
  };

  return (
    <AssistantSidePanelFormContainer
      onClose={handleClose}
      onSubmit={onRecalculateClick}
      closeLabel={<FormattedMessage id="common.close" defaultMessage="Close" />}
      submitLabel={<FormattedMessage id="common.recalculate" defaultMessage="Recalculate" />}
      submitDisabled={isLoading}
    >
      <div className={styles.carbonFormContainer}>
        <RadioButtonGroup
          name="bulk-update-type"
          value={bulkType}
          onChange={event => onRadioClick(event.target.value)}
          size="medium"
        >
          {showAutoPredictionsOnOff ? (
            <div>
              <RadioButton
                value="auto-predictions"
                label={
                  <FormattedMessage
                    id="predict.turn_auto_predictions_on_off"
                    defaultMessage="Turn auto-predictions on/off"
                  />
                }
                aria-label={intl.formatMessage({
                  id: 'predict.turn_auto_predictions_on_off',
                  defaultMessage: 'Turn auto-predictions on/off'
                })}
              />
              {bulkType === 'auto-predictions' && (
                <div style={{ marginLeft: 42 }}>
                  <Box display="flex" alignItems="center" mb="12px">
                    <Switch
                      checked={incomeIncluded}
                      onChange={handleIncomeIncludedChange}
                      mr="12px"
                      aria-label={intl.formatMessage({
                        id: 'financial_fields.category.income',
                        defaultMessage: 'Income'
                      })}
                    />
                    <Typography mb="0" fontWeight="500">
                      <FormattedMessage id="financial_fields.category.income" defaultMessage="Income" />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb="12px">
                    <Switch
                      checked={costOfSalesIncluded}
                      onChange={handleCostOfSalesIncludedChange}
                      mr="12px"
                      aria-label={intl.formatMessage({
                        id: 'financial_fields.category.cost_of_sales',
                        defaultMessage: 'Cost of Sales'
                      })}
                    />
                    <Typography mb="0" fontWeight="500">
                      <FormattedMessage id="financial_fields.category.cost_of_sales" defaultMessage="Cost of Sales" />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb="12px">
                    <Switch
                      checked={expensesIncluded}
                      onChange={handleExpensesIncludedChange}
                      mr="12px"
                      aria-label={intl.formatMessage({
                        id: 'financial_fields.category.expenses',
                        defaultMessage: 'Expenses'
                      })}
                    />
                    <Typography mb="0" fontWeight="500">
                      <FormattedMessage id="financial_fields.category.expenses" defaultMessage="Expenses" />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb="12px">
                    <Switch
                      checked={otherIncomeIncluded}
                      onChange={handleOtherIncomeIncludedChange}
                      mr="12px"
                      aria-label={intl.formatMessage({
                        id: 'financial_fields.category.other_income',
                        defaultMessage: 'Other Income'
                      })}
                    />
                    <Typography mb="0" fontWeight="500">
                      <FormattedMessage id="financial_fields.category.other_income" defaultMessage="Other Income" />
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mb="12px">
                    <Switch
                      checked={otherExpensesIncluded}
                      onChange={handleOtherExpensesIncludedChange}
                      mr="12px"
                      aria-label={intl.formatMessage({
                        id: 'financial_fields.category.other_expenses',
                        defaultMessage: 'Other Expenses'
                      })}
                    />
                    <Typography mb="0" fontWeight="500">
                      <FormattedMessage id="financial_fields.category.other_expenses" defaultMessage="Other Expenses" />
                    </Typography>
                  </Box>
                </div>
              )}
            </div>
          ) : null}
          <div className={styles.radioContainer} style={bulkType === 'manual-predictions' ? { marginTop: 12 } : {}}>
            <RadioButton
              value="manual-predictions"
              label={
                <FormattedMessage id="predict.turn_manual_predictions_on_off" defaultMessage="Turn manual predictions on/off" />
              }
              aria-label={intl.formatMessage({
                id: 'predict.turn_manual_predictions_on_off',
                defaultMessage: 'Turn manual predictions on/off'
              })}
            />

            {bulkType === 'manual-predictions' && (
              <div style={{ marginLeft: 42, marginTop: -10 }}>
                <Box mb="12px">
                  <FormattedMessage
                    id="predict.google_sheets_import_not_affected"
                    defaultMessage="Google sheets import templates will not be affected."
                  />
                </Box>
                <Box display="flex" alignItems="center" mb="12px">
                  <Switch
                    checked={incomeIncluded}
                    onChange={handleIncomeIncludedChange}
                    mr="12px"
                    aria-label={intl.formatMessage({ id: 'financial_fields.category.income', defaultMessage: 'Income' })}
                  />
                  <Typography mb="0" fontWeight="500">
                    <FormattedMessage id="financial_fields.category.income" defaultMessage="Income" />
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb="12px">
                  <Switch
                    checked={costOfSalesIncluded}
                    onChange={handleCostOfSalesIncludedChange}
                    mr="12px"
                    aria-label={intl.formatMessage({
                      id: 'financial_fields.category.cost_of_sales',
                      defaultMessage: 'Cost of Sales'
                    })}
                  />
                  <Typography mb="0" fontWeight="500">
                    <FormattedMessage id="financial_fields.category.cost_of_sales" defaultMessage="Cost of Sales" />
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb="12px">
                  <Switch
                    checked={expensesIncluded}
                    onChange={handleExpensesIncludedChange}
                    mr="12px"
                    aria-label={intl.formatMessage({
                      id: 'financial_fields.category.expenses',
                      defaultMessage: 'Expenses'
                    })}
                  />
                  <Typography mb="0" fontWeight="500">
                    <FormattedMessage id="financial_fields.category.expenses" defaultMessage="Expenses" />
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb="12px">
                  <Switch
                    checked={otherIncomeIncluded}
                    onChange={handleOtherIncomeIncludedChange}
                    mr="12px"
                    aria-label={intl.formatMessage({
                      id: 'financial_fields.category.other_income',
                      defaultMessage: 'Other Income'
                    })}
                  />
                  <Typography mb="0" fontWeight="500">
                    <FormattedMessage id="financial_fields.category.other_income" defaultMessage="Other Income" />
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb="12px">
                  <Switch
                    checked={otherExpensesIncluded}
                    onChange={handleOtherExpensesIncludedChange}
                    mr="12px"
                    aria-label={intl.formatMessage({
                      id: 'financial_fields.category.other_expenses',
                      defaultMessage: 'Other Expenses'
                    })}
                  />
                  <Typography mb="0" fontWeight="500">
                    <FormattedMessage id="financial_fields.category.other_expenses" defaultMessage="Other Expenses" />
                  </Typography>
                </Box>
              </div>
            )}
          </div>
        </RadioButtonGroup>
      </div>
    </AssistantSidePanelFormContainer>
  );
};
